import { MoreHoriz } from '@mui/icons-material';
import { Box, Checkbox, IconButton, List, ListItemIcon, ListItemText, MenuItem, Popover, Stack, Typography } from '@mui/material';
import StarFilledIcon from 'Components/Icons/StarFilledIcon';
import StarIcon from 'Components/Icons/StarIcon';
import TrashIcon from 'Components/Icons/TrashIcon';
import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { formatDateToDDMMYYYY } from 'helper/date.helper';
import { useDispatch, useSelector } from 'react-redux';

import {
    setActionType,
    setAllChecked,
    setItems,
    setModalOpen,
    setModalType,
    setReload,
    setSelectedItem,
    setSelectedItems
} from 'store/ResponseTemplate/actions';
import { SET_LOADING, SET_TOAST_OBJECT } from 'store/actions';
import { UPDATE_QUESTION_REGISTRY } from 'schemas/mutation';
import QuestionCardCheckboxIcon from 'Components/Icons/QuestionCardCheckboxIcon';
import QuestionCardCheckboxCheckedIcon from 'Components/Icons/QuestionCardCheckboxCheckedIcon';
import { checkIsPremiumPlan } from 'helper/checkPlan.helper';
import PermisssionValidationWrapper from 'Components/PermissionWrapper/PermissionValidationWrapper';
import { hasNoPermissionToEdit } from 'helper/permission.helper';
import { PermissionTypes } from 'Components/PermissionWrapper/constants';

const QuestionRegistryCard = ({ item }) => {
    const dispatch = useDispatch();
    const { drafted, lastEditedAt, starred, question, response } = item;
    const { reload, templateType, userSelectedItems, allQuestionRegistries, items } = useSelector((params) => params.response);
    const { companyData } = useSelector((params) => params.customization);
    const { pagePermissions } = useSelector((state) => state.authentication);
    const isEditNotPermitted = hasNoPermissionToEdit(PermissionTypes.questionRegistries, pagePermissions);
    /**Individual Starred Action Mutation */
    const [updateRegistry] = useMutation(UPDATE_QUESTION_REGISTRY);

    let date = lastEditedAt;
    let formateDate = formatDateToDDMMYYYY(date);

    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        dispatch({ type: setSelectedItem, selectedItem: item });
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const openPopover = Boolean(anchorEl);
    const id = openPopover ? 'question-delete-popover' : undefined;

    const findAndUpdateStarred = (id, status) => {
        const newItems = items?.map((item) => {
            if (item._id === id) {
                return { ...item, starred: status };
            }

            return item;
        });

        dispatch({ type: setItems, items: newItems });
    };

    /**Update Starred Individually */
    const updateStarred = async (e) => {
        e.stopPropagation();
        const status = item.starred === true ? 'unstarred' : 'starred';
        let variables = {
            editQuestionRegistryInput: {
                questionRegistryIds: [item._id],
                companyId: companyData._id,
                status
            }
        };
        dispatch({ type: SET_LOADING, loading: true });
        dispatch({ type: SET_TOAST_OBJECT, toastObject: { status: false } });

        try {
            await updateRegistry({
                variables: { ...variables }
            });
            await findAndUpdateStarred(item?._id, !item.starred);
            dispatch({ type: setReload, reload: !reload });
        } catch (e) {
            dispatch({
                type: SET_TOAST_OBJECT,
                toastObject: {
                    status: true,
                    message: e.message || 'Something went wrong. Please try again later.',
                    variant: 'error'
                }
            });
        } finally {
            dispatch({ type: SET_LOADING, loading: false });
        }
    };

    const findItem = (item) => {
        const findItem = userSelectedItems.find((selectedItem) => selectedItem._id === item._id);
        return findItem ? true : false;
    };

    const handleCheckedClicked = (e) => {
        e.stopPropagation();
        let newItems = [...userSelectedItems];
        if (userSelectedItems.length) {
            const findItem = userSelectedItems.find((selectedItem) => selectedItem._id === item._id);
            if (findItem) {
                newItems = userSelectedItems.filter((selectedItem) => selectedItem._id !== item._id);
            } else {
                newItems.push({ _id: item._id, starred: item?.starred });
            }
        } else {
            newItems.push({ _id: item._id, starred: item?.starred });
        }
        dispatch({ type: setSelectedItems, userSelectedItems: newItems });
        if (newItems.length !== allQuestionRegistries.length) dispatch({ type: setAllChecked, isAllChecked: false });
        else dispatch({ type: setAllChecked, isAllChecked: true });
    };

    const handleDelete = () => {
        dispatch({ type: setModalOpen, modalOpen: true });
        dispatch({ type: setModalType, modalType: 'delete' });
        dispatch({ type: setActionType, typeOfAction: 'I' });
        handleClose();
    };

    return (
        <>
            <Stack
                gap="10px"
                className={`question-registry-card${findItem(item) ? ' checked' : ''}${templateType === 'deleted' ? ' deleted-card' : ''}`}
                justifyContent="space-between"
                onClick={() => {
                    dispatch({ type: setSelectedItem, selectedItem: item });

                    setTimeout(() => {
                        dispatch({ type: setModalOpen, modalOpen: true });
                        dispatch({ type: setModalType, modalType: 'edit' });
                    }, 0);
                }}
            >
                <PermisssionValidationWrapper pageName={PermissionTypes.questionRegistries}>
                    <Checkbox
                        checked={findItem(item)}
                        icon={<QuestionCardCheckboxIcon />}
                        checkedIcon={<QuestionCardCheckboxCheckedIcon />}
                        onClick={handleCheckedClicked}
                    />
                </PermisssionValidationWrapper>
                {checkIsPremiumPlan(companyData?.subscription?.plan) && templateType !== 'deleted' && (
                    <IconButton
                        className={`${starred ? 'active' : ''} star-icon`}
                        onClick={updateStarred}
                        disableRipple
                        disabled={isEditNotPermitted}
                    >
                        {starred ? <StarFilledIcon /> : <StarIcon />}
                    </IconButton>
                )}
                {templateType !== 'deleted' && (
                    <PermisssionValidationWrapper pageName={PermissionTypes.questionRegistries}>
                        <IconButton className="three-dots" onClick={handleClick} disableRipple>
                            <MoreHoriz className="dot" />
                        </IconButton>
                    </PermisssionValidationWrapper>
                )}
                <Stack gap="10px" className="text">
                    {question && (
                        <Typography
                            variant="subtitle1"
                            component="h3"
                            color={`${drafted ? '#7D7D82' : '#00214D'}`}
                            fontSize="1rem"
                            lineHeight="1.2"
                            fontWeight="600"
                        >
                            {drafted ? <span>Draft</span> : ''}
                            {question}
                        </Typography>
                    )}
                    {response && (
                        <Typography variant="caption" component="p" color="#676767" fontSize="0.875rem" lineHeight="1.3809">
                            {response}
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                    <Typography fontSize="0.875rem" color="#7D7D82" fontWeight="500">
                        Last edited
                    </Typography>
                    <Typography fontSize="0.875rem" color="#7D7D82">
                        {/* 21/1/2024 */}
                        {formateDate}
                    </Typography>
                </Stack>
            </Stack>
            <Popover
                id={id}
                open={openPopover}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                className="question-delete-popover"
                sx={{
                    width: '200px'
                }}
            >
                <Box className="popover-wrap">
                    <List>
                        <MenuItem onClick={handleDelete} disableRipple>
                            <ListItemIcon>{<TrashIcon />}</ListItemIcon>
                            <ListItemText>Delete question</ListItemText>
                        </MenuItem>
                    </List>
                </Box>
            </Popover>
        </>
    );
};

export default QuestionRegistryCard;
